import type {
  ServiceRequestBulkBody,
  ServiceRequestBulkResultDto,
  ServiceRequestBulkStatusDto,
  ServiceRequestWithMailDto,
  ApproveServiceEstimateInput,
  CompleteServiceRequestInput,
  CreateServiceRequestInput,
  DeclineServiceRequestInput,
  PutServiceConsumablesInput,
  PutServiceEstimateBreakdownInput,
  PutServiceEstimateInput,
  PutServiceExecutorsInput,
  RequestHistoryEntryDto,
  ServiceFileKind,
  ServiceRequestDto,
  MarkServiceChatReadInput,
  OpenServiceEstimateDisputeInput,
  ResolveServiceEstimateDisputeInput,
  SendServiceChatMessageInput,
  ServiceChatMessageDto,
  ServiceChatPageDto,
  ServiceStatusChangeInput,
  ServiceWarrantyRowDto,
  SetServiceConsumablesIssuedInput,
  SetServiceUrgencyInput,
  SubmitServiceEstimateInput,
  UpdateServiceRequestInput,
} from '@technic/contracts';
import {
  apiFetch,
  createGetApi,
  createListApi,
  createRemoveApi,
  createWriteApi,
  type ListResult,
  type Query,
} from '@shared/api';
import type { RequestCreateReconciliation } from '@shared/lib';

const PATH = '/service-requests';

/** Тело перехода без данных: версия сверяется в `WHERE`, оптимистическая блокировка (Р30). */
interface VersionOnly {
  version: number;
}

/** Переход, отменяющий чужую работу: причина обязательна (§5.3). */
interface ReasonInput extends VersionOnly {
  reason: string;
}

/** Приёмка: комментарий необязателен — решение выражено самим переходом. */
interface CommentInput extends VersionOnly {
  comment?: string;
}

const patch = <T>(id: string, tail: string, body: unknown) =>
  apiFetch<T>(`${PATH}/${id}${tail}`, { method: 'PATCH', body });

/**
 * Заявки на обслуживание оргтехники (ADR 0085).
 *
 * Каждый переход с содержанием — своя ручка, а не общий `changeStatus` с полем «данные» (§8.1):
 * так проверка условия перехода на сервере не может разъехаться с проверкой данных этого
 * перехода, а портал не имеет возможности отправить объём работ туда, где его не ждут.
 * `changeStatus` остаётся отмене и административным откатам — у них из содержания только причина.
 *
 * Все изменяющие ручки принимают `version` (Р30): между открытием карточки и нажатием кнопки
 * заявку мог продвинуть другой человек, и молча затирать его решение нельзя.
 *
 * Ответ у переходов один и тот же — обновлённая заявка: следующее действие (предъявить объём
 * работ сразу после его правки) требует новой версии, а перезапрашивать карточку ради номера
 * версии значило бы гонку с самим собой.
 */
// cache-home: service-requests — every write here changes a request; `service-executor-candidates`
// is derived from accounts and grants, which this object does not write.
export const serviceRequestsApi = {
  ...createListApi<ServiceRequestDto>(PATH),
  ...createGetApi<ServiceRequestDto>(PATH),
  ...createWriteApi<ServiceRequestDto, CreateServiceRequestInput, UpdateServiceRequestInput>(PATH),
  /**
   * Заведение отвечает заявкой **и исходом письма службе**: у службы нет учётки в портале, и
   * «заявка заведена, но служба не оповещена» человек обязан узнать сразу, а не когда за ней не
   * приехали. Переопределяет `create` фабрики — форма ответа у него своя.
   *
   * КЛЮЧ ИДЕМПОТЕНТНОСТИ — ЗАГОЛОВКОМ И ТОЛЬКО У ВЕТКИ КАНДИДАТА (план
   * `docs/office-equipment-candidate-plan.md`, §8): тело с `equipmentCandidate` заводит СРАЗУ ДВЕ
   * строки — заявку и сообщение о технике, — и потерянный успешный ответ стоил бы человеку второй
   * пары. Обычная заявка ключа не несёт: маршрут его там не ждёт, а обязательный для всех ключ
   * означал бы правку каждого сегодняшнего вызова ради ветки, которой у большинства нет.
   *
   * Ключ приходит параметром, а не рождается здесь: он описывает ПОПЫТКУ, и повтор после обрыва
   * обязан нести тот же ключ. Роди его этот метод — каждое нажатие получало бы свой, и защита
   * выродилась бы в украшение (тот же довод, что у заведения плановой закупки).
   */
  create: (body: CreateServiceRequestInput, idempotencyKey?: string) => {
    const options = {
      method: 'POST',
      body,
      ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
    } as const;
    return idempotencyKey && !body.equipmentCandidate
      ? apiFetch<ServiceRequestWithMailDto>(`${PATH}/_create-with-receipt`, options)
      : apiFetch<ServiceRequestWithMailDto>(PATH, options);
  },
  reconcileCreate: (body: CreateServiceRequestInput, idempotencyKey: string) =>
    apiFetch<RequestCreateReconciliation>(`${PATH}/_create-attempts/reconcile`, {
      method: 'POST',
      body: { originalBody: body },
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  ...createRemoveApi<{ ok: boolean }>(PATH),

  history: (id: string) => apiFetch<RequestHistoryEntryDto[]>(`${PATH}/${id}/history`),

  /**
   * Реестр действующих гарантий (§9.5): строки двух видов — гарантия поставщика на единицу и
   * гарантия на выполненную позицию ремонта. Своей ручки создания у реестра нет: он отвечает на
   * вопрос «что ещё покрыто», а обращение по гарантии заводится обычной заявкой с источником.
   */
  warranties: (query: Query) =>
    apiFetch<ListResult<ServiceWarrantyRowDto>>(`${PATH}/warranties`, { query }),

  /**
   * Сколько заявок ждёт решения самого спрашивающего — число для бейджа в меню. Счётчиком, а не
   * первой страницей списка: бейдж рисуется в каркасе портала на любом экране, а сами заявки
   * нужны уже в разделе. Сторону считает сервер по правам учётки — портал её не передаёт.
   */
  waitingCount: () => apiFetch<{ count: number }>(`${PATH}/waiting-count`),

  /**
   * Обсуждение заявки (ADR 0141). Лента страничная и курсорная: `beforeSeq` — подгрузка вверх,
   * `afterSeq` — инкрементальный опрос открытого окна. Смещения у ленты нет и быть не может — она
   * только растёт, и номер страницы съезжал бы на каждую пришедшую реплику.
   */
  chatPage: (id: string, query: { beforeSeq?: number; afterSeq?: number; limit?: number } = {}) =>
    apiFetch<ServiceChatPageDto>(`${PATH}/${id}/messages`, { query }),
  /** Ответ — только созданная реплика и новый `lastSeq`: возвращать полсотни ради одной незачем. */
  sendChatMessage: (id: string, body: SendServiceChatMessageInput) =>
    apiFetch<{ message: ServiceChatMessageDto; lastSeq: number }>(`${PATH}/${id}/messages`, {
      method: 'POST',
      body,
    }),
  /**
   * Подтверждение прочтения — курсор, а не отметка времени (ADR 0141, решение 5). Зовётся ПОСЛЕ
   * успешного показа ленты: отметка на открытии гасила бы разговор и тогда, когда загрузка упала и
   * человек не увидел ничего.
   */
  markChatRead: (id: string, body: MarkServiceChatReadInput) =>
    apiFetch<{ readThroughSeq: number; lastSeq: number }>(`${PATH}/${id}/messages/read`, {
      method: 'POST',
      body,
    }),
  /**
   * «Отметить все прочитанными» по заявкам текущего отбора. Тело — те же параметры, что у списка:
   * кнопка обязана гасить ровно то, что человек видит, а не всё подряд.
   */
  markAllChatRead: (query: Query) =>
    apiFetch<{ count: number }>(`${PATH}/messages/read-all`, { method: 'POST', body: query }),
  /**
   * Сколько заявок несут непрочитанное, адресованное мне, — число для синего бейджа раздела.
   * Отдельно от «ждут меня» (`waitingCount`): вопросы разные, и сумма не отвечает ни на один.
   */
  chatUnreadCount: () => apiFetch<{ count: number }>(`${PATH}/unread-count`),

  /**
   * Исполнители заявки одним действием (Н5, Н6): список своих сотрудников **и**
   * исполнитель-контрагент. Оба поля уходят целиком — это состав, а не добавление: «прислать
   * одного» означало бы, что сервер угадывает, снимали ли кого-то.
   *
   * Ответ несёт исход письма о назначении: оно адресовано **людям**, а не ящику службы, и
   * «назначили, но задание никуда не ушло» назначивший обязан узнать сразу — иначе он будет ждать
   * работу от того, кто о ней не знает.
   */
  putExecutors: (id: string, body: PutServiceExecutorsInput) =>
    apiFetch<ServiceRequestWithMailDto>(`${PATH}/${id}/executors`, { method: 'PUT', body }),
  /**
   * Прежнее назначение контрагента. Остаётся совместимым адаптером на весь выпуск 1 и уходит
   * вместе с ним (план §7.3): её зовёт вкладка, открытая до выката, — портал ходит только новой
   * ручкой выше.
   */
  /** Отказ исполнителя: заявка снова ничья, у неё стирается сервис. */
  decline: (id: string, body: DeclineServiceRequestInput) =>
    patch<ServiceRequestDto>(id, '/decline', body),
  /** Взятие в диагностику — единственный переход исполнителя без содержания. */
  start: (id: string, body: VersionOnly) => patch<ServiceRequestDto>(id, '/start', body),
  /**
   * Заморозка (Р103): своя дуга — своя ручка. Причина обязательна (Р107) — даты «отложена до» у
   * заморозки нет, и на вопрос «когда ждать» отвечает только она. Куда вернуть, портал не
   * присылает: исходный статус сервер берёт из самой заявки (Р104).
   */
  hold: (id: string, body: ReasonInput) => patch<ServiceRequestDto>(id, '/hold', body),
  /** Возврат в работу: цель — `held_from_status`, от человека нужно лишь слово вдогонку. */
  resume: (id: string, body: CommentInput) => patch<ServiceRequestDto>(id, '/resume', body),

  /**
   * Состав объёма работ целиком: это документ, и «добавить строку» без остальных строк не имеет
   * смысла. Поэтому PUT, а не POST по строке. Имена ручек и полей при этом прежние (`estimate`,
   * Р17): переименовалось слово для человека, а не схема базы и матрица прав.
   *
   * Ручка отвечает 409, пока висит непогашенное предъявление (Р9): статуса-замка больше нет, и
   * править цифры под открытым окном согласования нельзя — согласующий подписал бы не то, что
   * видел. Ключ от замка один — «вернуть в правку» ниже.
   */
  saveEstimate: (id: string, body: PutServiceEstimateInput) =>
    apiFetch<ServiceRequestDto>(`${PATH}/${id}/estimate`, { method: 'PUT', body }),
  /**
   * Раскладка свободной записи по графам (план
   * `docs/office-equipment-free-estimate-and-executor-scope-plan.md`, Р2; ответ В9 заказчика от
   * 09.09.2026). Тело по составу то же, что у соседки выше, — и это РАЗНЫЕ двери, а не одна с
   * флагом.
   *
   * Соседку открывает `serviceRequests.estimate` — автор объёма работ, исполнитель, — и правит он
   * черновик: состав заменяется, номер ревизии и подписи не трогаются. Эту открывает
   * `serviceRequests.estimateRewrite` — «Ведение», переносящее присланный подрядчиком перечень в
   * графы, — и по СОГЛАСОВАННОЙ ревизии она делает другое: поднимает номер, снимает подпись,
   * пересчитывает итог и ставит новое предъявление, потому что подписанное содержимое под прежней
   * ревизией не меняется (ADR 0133). Заявка после неё ждёт новой подписи.
   *
   * Отказы у неё свои, и портал их не предугадывает: 409 под висящим предъявлением (согласующий
   * подписал бы не то, что видел) и 422 вне статуса «В работе» — отложенную сперва возобновляют, у
   * «Новой» объёма работ ещё нет, а после закрытия работ строки несут факт и гарантии.
   */
  saveEstimateBreakdown: (id: string, body: PutServiceEstimateBreakdownInput) =>
    apiFetch<ServiceRequestDto>(`${PATH}/${id}/estimate/breakdown`, { method: 'PUT', body }),
  /**
   * Предъявление объёма работ: ревизия +1, либо гарантийный ремонт без оплаты (Р27). Статуса не
   * двигает (Р8) — поднимает ревизию и ставит непогашенное предъявление, по которому очередь
   * переводит заявку на сторону согласования. Повторное предъявление заперто тем же 409 (Р9):
   * иначе исполнитель подменил бы снимок суммы под уже открытым окном согласования.
   */
  submitEstimate: (id: string, body: SubmitServiceEstimateInput) =>
    patch<ServiceRequestDto>(id, '/estimate/submit', body),
  /**
   * Согласование и отклонение — одна ручка: у них одно право, одна область и один момент. Статуса
   * согласие не двигает (Р8) — гасит предъявление, и заявка остаётся «В работе»; отказ, наоборот,
   * закрывает её в «Отменена» и несёт с собой причину и решение — «что делаем вместо» (Р12).
   */
  decideEstimate: (id: string, body: ApproveServiceEstimateInput) =>
    patch<ServiceRequestDto>(id, '/estimate/approval', body),
  /**
   * Возврат объёма работ в правку (Р9) — единственный путь изменить состав после предъявления.
   * Снимает обе отметки: подпись согласования (как раньше) и непогашенное предъявление, — поэтому
   * ручка отпирает и правку состава, и повторное предъявление сразу.
   */
  reopenEstimate: (id: string, body: ReasonInput) =>
    patch<ServiceRequestDto>(id, '/estimate/reopen', body),

  /**
   * Спор об освобождении от подписи — две ручки, а не одна с полем «исход» (Р9 плана
   * `docs/office-equipment-on-site-and-invoice-estimate-plan.md`).
   *
   * Открытие останавливает заявку заморозкой с видом «спор» и требует причину: ею объясняют
   * исполнителю, почему работу остановили. Разрешение несёт исход, а КУДА от него уйдёт заявка,
   * решает матрица сервера («откуда открыт × исход»): портал про эти статусы не знает и знать не
   * должен — вторая матрица разошлась бы с первой молча.
   */
  openEstimateDispute: (id: string, body: OpenServiceEstimateDisputeInput) =>
    patch<ServiceRequestDto>(id, '/estimate/dispute', body),
  resolveEstimateDispute: (id: string, body: ResolveServiceEstimateDisputeInput) =>
    patch<ServiceRequestDto>(id, '/estimate/dispute/resolution', body),

  /**
   * Состав строк номенклатуры целиком (Н9), как и объём работ: это список того, что просят, и
   * «добавить одну позицию» без остальных заставляло бы сервер угадывать, снимали ли что-то. Пустой
   * список
   * ручка не принимает — заявка на расходники без строк это заявка без предмета.
   *
   * Правится он только до первой отметки о выдаче: строка, за которой числится движение склада, —
   * уже основание записи на складе, и сервер отвечает на такую правку 409.
   */
  putConsumables: (id: string, body: PutServiceConsumablesInput) =>
    apiFetch<ServiceRequestDto>(`${PATH}/${id}/consumables`, { method: 'PUT', body }),
  /**
   * Правка факта выдачи (Р6). Склад двигает **изменение факта**, а не смена статуса: каждая правка
   * порождает событие журнала на разницу — было 2, стало 3, значит со склада уйдёт одна штука.
   * Отсюда `PATCH` и только тронутые строки: состава заявки эта ручка не касается.
   */
  setConsumablesIssued: (id: string, body: SetServiceConsumablesIssuedInput) =>
    patch<ServiceRequestDto>(id, '/consumables/issued', body),

  /** Закрытие работ: отметки факта по строкам. Итог не передаётся — его считает сервер (Р12). */
  complete: (id: string, body: CompleteServiceRequestInput) =>
    patch<ServiceRequestDto>(id, '/complete', body),
  accept: (id: string, body: CommentInput) => patch<ServiceRequestDto>(id, '/accept', body),
  rework: (id: string, body: ReasonInput) => patch<ServiceRequestDto>(id, '/rework', body),
  /*
   * Визы отдела ИТ здесь больше нет (ADR 0145, Р10): подпись под объёмом работ одна, и ставит её
   * назначенный сотрудник — `decideEstimate`. Ручка `/it-approval` снята и с сервера; клиентский
   * метод, оставленный «на всякий случай», звал бы её в 404.
   *
   * Поля визы у заявки при этом живы и читаются: подпись, поставленная до этого выпуска, — правда
   * о том, что тогда решили, и карточка обязана её показывать.
   */
  /** Только отмена и административные откаты (Р18) — остальное ходит своими ручками. */
  /** Отмена и откаты: ответ несёт исход письма — отменённую заявку служба тоже должна узнать. */
  changeStatus: (id: string, body: ServiceStatusChangeInput) =>
    patch<ServiceRequestWithMailDto>(id, '/status', body),
  /**
   * Срочность своей ручкой, а не полем правки (Р56): её ставят и снимают до самого закрытия, в том
   * числе когда саму заявку править уже нельзя.
   */
  setUrgency: (id: string, body: SetServiceUrgencyInput) =>
    patch<ServiceRequestDto>(id, '/urgency', body),

  /** Подшивка документа: вид говорит, чем именно закрыта работа (§8.3). */
  attachFiles: (id: string, fileIds: string[], kind: ServiceFileKind) =>
    apiFetch<ServiceRequestDto>(`${PATH}/${id}/files`, {
      method: 'POST',
      body: { fileIds, kind },
    }),
  detachFile: (id: string, fileId: string) =>
    apiFetch<{ ok: boolean }>(`${PATH}/${id}/files/${fileId}`, { method: 'DELETE' }),

  /**
   * Пакетная команда над несколькими заявками сразу (план
   * `docs/office-equipment-bulk-actions-plan.md`, Р1): одна ручка на все операции, тело —
   * размеченный союз по `operation`, строки — парами «идентификатор + версия» (Р4).
   *
   * КЛЮЧ ИДЕМПОТЕНТНОСТИ ОБЯЗАТЕЛЕН и приходит параметром, а не рождается здесь. Пачка идёт
   * секунды, кнопка всё это время видна, вкладка может перезагрузиться — повтор той же попытки
   * обязан нести ТОТ ЖЕ ключ, иначе полсотни чужих заявок отменились бы дважды. Версии от этого
   * не спасают: повтор после успеха выглядел бы как «ничего не вышло», хотя всё вышло (Р7).
   *
   * Ответ приходит один на всю пачку и построчный: пакет — это N независимых исходов, и «done» у
   * сорока девяти строк не отменяется чужой версией пятидесятой (Р3).
   */
  bulk: (body: ServiceRequestBulkBody, idempotencyKey: string) =>
    apiFetch<ServiceRequestBulkResultDto>(`${PATH}/bulk`, {
      method: 'POST',
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  /**
   * Состояние СВОЕЙ пачки по тому же ключу (Н12): сколько строк уже обработано и готовый отчёт,
   * когда пачка закончилась. Нужна двоим — прогрессу открытого окна и вкладке, перезагруженной
   * посреди операции: синхронный ответ `POST` о ходе дела молчит, а после обрыва не приходит вовсе.
   */
  bulkStatus: (idempotencyKey: string) =>
    apiFetch<ServiceRequestBulkStatusDto>(`${PATH}/bulk/${idempotencyKey}`),

  /** Возврат из архива (ADR 0070) и удаление насовсем (ADR 0060) — слова портала, не HTTP. */
  restore: (id: string) => apiFetch<ServiceRequestDto>(`${PATH}/${id}/restore`, { method: 'POST' }),
  purge: (id: string) => apiFetch<{ ok: boolean }>(`${PATH}/${id}/purge`, { method: 'DELETE' }),
};
